import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { APIListWrapper } from '../../models/common/api-list-wrapper.model';
import { APIResponseWrapper } from '../../models/common/api-response-wrapper';
import { MaintenanceAlert, MaintenanceAnalyticsDashboard } from './models/maintenance-analytics.model';
import { MaintenanceAvailabilityCheckRequest, MaintenanceAvailabilityDecision, MaintenanceDashboard, MaintenanceFinanceSummary, MaintenancePartAvailability, MaintenancePartReorderRecommendation, MaintenanceVehicleAvailability } from './models/maintenance-availability.model';
import { MaintenanceInspectionDefect, MaintenanceInspectionForm, MaintenanceInspectionSubmission } from './models/maintenance-inspection.model';
import { MaintenanceDueItem, MaintenanceMeterReading, MaintenanceServiceProgram } from './models/maintenance-program.model';
import { MaintenanceRequest, MaintenanceStatusAction } from './models/maintenance-request.model';
import { MaintenanceFinanceAction, MaintenancePartAction, MaintenanceWorkOrder, MaintenanceWorkOrderLinkAction } from './models/maintenance-work-order.model';

export interface MaintenanceSearchParams {
  owningCompanyId?: number;
  page?: number;
  pageSize?: number;
  status?: string;
  severity?: string;
  priority?: string;
  vehicleId?: number;
  driverId?: number;
  vehicleTypeId?: number;
  requestId?: number;
  programId?: number;
  formId?: number;
  submissionId?: number;
  source?: string;
  purchaseType?: string;
  keyword?: string;
}

@Injectable({ providedIn: 'root' })
export class MaintenanceService {
  private readonly baseUrl = `${environment.restApiUrl}fleet-maintenance`;

  constructor(private http: HttpClient) {}

  getDashboard(owningCompanyId: number): Observable<APIResponseWrapper<MaintenanceDashboard>> {
    return this.http.get<APIResponseWrapper<MaintenanceDashboard>>(`${this.baseUrl}/dashboard`, {
      params: new HttpParams().set('owningCompanyId', owningCompanyId)
    });
  }

  getAnalytics(owningCompanyId: number, periodFrom?: Date | string, periodTo?: Date | string, vehicleId?: number): Observable<APIResponseWrapper<MaintenanceAnalyticsDashboard>> {
    let params = new HttpParams().set('owningCompanyId', owningCompanyId);
    params = this.appendIfPresent(params, 'periodFrom', periodFrom);
    params = this.appendIfPresent(params, 'periodTo', periodTo);
    params = this.appendIfPresent(params, 'vehicleId', vehicleId);
    return this.http.get<APIResponseWrapper<MaintenanceAnalyticsDashboard>>(`${this.baseUrl}/analytics`, { params });
  }

  getAlerts(owningCompanyId: number, periodFrom?: Date | string, periodTo?: Date | string, vehicleId?: number): Observable<APIListWrapper<MaintenanceAlert>> {
    let params = new HttpParams().set('owningCompanyId', owningCompanyId);
    params = this.appendIfPresent(params, 'periodFrom', periodFrom);
    params = this.appendIfPresent(params, 'periodTo', periodTo);
    params = this.appendIfPresent(params, 'vehicleId', vehicleId);
    return this.http.get<APIListWrapper<MaintenanceAlert>>(`${this.baseUrl}/alerts`, { params });
  }

  searchRequests(filters: MaintenanceSearchParams): Observable<APIListWrapper<MaintenanceRequest>> {
    return this.http.get<APIListWrapper<MaintenanceRequest>>(`${this.baseUrl}/requests`, { params: this.searchParams(filters) });
  }

  createRequest(request: MaintenanceRequest): Observable<APIResponseWrapper<MaintenanceRequest>> {
    return this.http.post<APIResponseWrapper<MaintenanceRequest>>(`${this.baseUrl}/requests`, request);
  }

  getRequest(id: number): Observable<APIResponseWrapper<MaintenanceRequest>> {
    return this.http.get<APIResponseWrapper<MaintenanceRequest>>(`${this.baseUrl}/requests/${id}`);
  }

  updateRequest(id: number, request: MaintenanceRequest): Observable<APIResponseWrapper<MaintenanceRequest>> {
    return this.http.put<APIResponseWrapper<MaintenanceRequest>>(`${this.baseUrl}/requests/${id}`, request);
  }

  convertRequestToWorkOrder(id: number, workOrder?: MaintenanceWorkOrder): Observable<APIResponseWrapper<MaintenanceWorkOrder>> {
    return this.http.post<APIResponseWrapper<MaintenanceWorkOrder>>(`${this.baseUrl}/requests/${id}/convert-to-work-order`, workOrder ?? {});
  }

  searchWorkOrders(filters: MaintenanceSearchParams): Observable<APIListWrapper<MaintenanceWorkOrder>> {
    return this.http.get<APIListWrapper<MaintenanceWorkOrder>>(`${this.baseUrl}/work-orders`, { params: this.searchParams(filters) });
  }

  createWorkOrder(workOrder: MaintenanceWorkOrder): Observable<APIResponseWrapper<MaintenanceWorkOrder>> {
    return this.http.post<APIResponseWrapper<MaintenanceWorkOrder>>(`${this.baseUrl}/work-orders`, workOrder);
  }

  getWorkOrder(id: number): Observable<APIResponseWrapper<MaintenanceWorkOrder>> {
    return this.http.get<APIResponseWrapper<MaintenanceWorkOrder>>(`${this.baseUrl}/work-orders/${id}`);
  }

  updateWorkOrder(id: number, workOrder: MaintenanceWorkOrder): Observable<APIResponseWrapper<MaintenanceWorkOrder>> {
    return this.http.put<APIResponseWrapper<MaintenanceWorkOrder>>(`${this.baseUrl}/work-orders/${id}`, workOrder);
  }

  updateWorkOrderStatus(id: number, action: MaintenanceStatusAction): Observable<APIResponseWrapper<MaintenanceWorkOrder>> {
    return this.http.post<APIResponseWrapper<MaintenanceWorkOrder>>(`${this.baseUrl}/work-orders/${id}/status`, action);
  }

  postWorkOrderFinance(id: number, action: MaintenanceFinanceAction): Observable<APIResponseWrapper<MaintenanceWorkOrder>> {
    return this.http.post<APIResponseWrapper<MaintenanceWorkOrder>>(`${this.baseUrl}/work-orders/${id}/post-finance`, action);
  }

  linkExpense(id: number, action: MaintenanceWorkOrderLinkAction): Observable<APIResponseWrapper<MaintenanceWorkOrder>> {
    return this.http.post<APIResponseWrapper<MaintenanceWorkOrder>>(`${this.baseUrl}/work-orders/${id}/link-expense`, action);
  }

  reservePart(workOrderId: number, partLineId: number, action: MaintenancePartAction): Observable<APIResponseWrapper<MaintenanceWorkOrder>> {
    return this.http.post<APIResponseWrapper<MaintenanceWorkOrder>>(`${this.baseUrl}/work-orders/${workOrderId}/parts/${partLineId}/reserve`, action);
  }

  issuePart(workOrderId: number, partLineId: number, action: MaintenancePartAction): Observable<APIResponseWrapper<MaintenanceWorkOrder>> {
    return this.http.post<APIResponseWrapper<MaintenanceWorkOrder>>(`${this.baseUrl}/work-orders/${workOrderId}/parts/${partLineId}/issue`, action);
  }

  consumePart(workOrderId: number, partLineId: number, action: MaintenancePartAction): Observable<APIResponseWrapper<MaintenanceWorkOrder>> {
    return this.http.post<APIResponseWrapper<MaintenanceWorkOrder>>(`${this.baseUrl}/work-orders/${workOrderId}/parts/${partLineId}/consume`, action);
  }

  returnPart(workOrderId: number, partLineId: number, action: MaintenancePartAction): Observable<APIResponseWrapper<MaintenanceWorkOrder>> {
    return this.http.post<APIResponseWrapper<MaintenanceWorkOrder>>(`${this.baseUrl}/work-orders/${workOrderId}/parts/${partLineId}/return`, action);
  }

  searchPrograms(filters: MaintenanceSearchParams): Observable<APIListWrapper<MaintenanceServiceProgram>> {
    return this.http.get<APIListWrapper<MaintenanceServiceProgram>>(`${this.baseUrl}/service-programs`, { params: this.searchParams(filters) });
  }

  createProgram(program: MaintenanceServiceProgram): Observable<APIResponseWrapper<MaintenanceServiceProgram>> {
    return this.http.post<APIResponseWrapper<MaintenanceServiceProgram>>(`${this.baseUrl}/service-programs`, program);
  }

  searchMeterReadings(filters: MaintenanceSearchParams): Observable<APIListWrapper<MaintenanceMeterReading>> {
    return this.http.get<APIListWrapper<MaintenanceMeterReading>>(`${this.baseUrl}/meter-readings`, { params: this.searchParams(filters) });
  }

  createMeterReading(reading: MaintenanceMeterReading): Observable<APIResponseWrapper<MaintenanceMeterReading>> {
    return this.http.post<APIResponseWrapper<MaintenanceMeterReading>>(`${this.baseUrl}/meter-readings`, reading);
  }

  searchDueItems(filters: MaintenanceSearchParams): Observable<APIListWrapper<MaintenanceDueItem>> {
    return this.http.get<APIListWrapper<MaintenanceDueItem>>(`${this.baseUrl}/due-items`, { params: this.searchParams(filters) });
  }

  recalculateDueItems(owningCompanyId: number, vehicleId?: number, programId?: number): Observable<APIResponseWrapper<MaintenanceDueItem[]>> {
    let params = new HttpParams().set('owningCompanyId', owningCompanyId);
    params = this.appendIfPresent(params, 'vehicleId', vehicleId);
    params = this.appendIfPresent(params, 'programId', programId);
    return this.http.post<APIResponseWrapper<MaintenanceDueItem[]>>(`${this.baseUrl}/due-items/recalculate`, null, { params });
  }

  completeDueItem(id: number, action?: MaintenanceStatusAction): Observable<APIResponseWrapper<MaintenanceDueItem>> {
    return this.http.post<APIResponseWrapper<MaintenanceDueItem>>(`${this.baseUrl}/due-items/${id}/complete`, action ?? {});
  }

  skipDueItem(id: number, action?: MaintenanceStatusAction): Observable<APIResponseWrapper<MaintenanceDueItem>> {
    return this.http.post<APIResponseWrapper<MaintenanceDueItem>>(`${this.baseUrl}/due-items/${id}/skip`, action ?? {});
  }

  searchInspectionForms(filters: MaintenanceSearchParams): Observable<APIListWrapper<MaintenanceInspectionForm>> {
    return this.http.get<APIListWrapper<MaintenanceInspectionForm>>(`${this.baseUrl}/inspection-forms`, { params: this.searchParams(filters) });
  }

  createInspectionForm(form: MaintenanceInspectionForm): Observable<APIResponseWrapper<MaintenanceInspectionForm>> {
    return this.http.post<APIResponseWrapper<MaintenanceInspectionForm>>(`${this.baseUrl}/inspection-forms`, form);
  }

  searchInspectionSubmissions(filters: MaintenanceSearchParams): Observable<APIListWrapper<MaintenanceInspectionSubmission>> {
    return this.http.get<APIListWrapper<MaintenanceInspectionSubmission>>(`${this.baseUrl}/inspection-submissions`, { params: this.searchParams(filters) });
  }

  createInspectionSubmission(submission: MaintenanceInspectionSubmission): Observable<APIResponseWrapper<MaintenanceInspectionSubmission>> {
    return this.http.post<APIResponseWrapper<MaintenanceInspectionSubmission>>(`${this.baseUrl}/inspection-submissions`, submission);
  }

  searchDefects(filters: MaintenanceSearchParams): Observable<APIListWrapper<MaintenanceInspectionDefect>> {
    return this.http.get<APIListWrapper<MaintenanceInspectionDefect>>(`${this.baseUrl}/defects`, { params: this.searchParams(filters) });
  }

  resolveDefect(id: number, action?: MaintenanceStatusAction): Observable<APIResponseWrapper<MaintenanceInspectionDefect>> {
    return this.http.post<APIResponseWrapper<MaintenanceInspectionDefect>>(`${this.baseUrl}/defects/${id}/resolve`, action ?? {});
  }

  linkDefectToWorkOrder(id: number, workOrder?: MaintenanceWorkOrder): Observable<APIResponseWrapper<MaintenanceInspectionDefect>> {
    return this.http.post<APIResponseWrapper<MaintenanceInspectionDefect>>(`${this.baseUrl}/defects/${id}/link-work-order`, workOrder ?? {});
  }

  searchAvailability(filters: MaintenanceSearchParams & { availabilityStatus?: string }): Observable<APIListWrapper<MaintenanceVehicleAvailability>> {
    let params = this.searchParams(filters);
    params = this.appendIfPresent(params, 'availabilityStatus', filters.availabilityStatus);
    return this.http.get<APIListWrapper<MaintenanceVehicleAvailability>>(`${this.baseUrl}/availability`, { params });
  }

  checkAvailability(request: MaintenanceAvailabilityCheckRequest): Observable<APIResponseWrapper<MaintenanceAvailabilityDecision>> {
    return this.http.post<APIResponseWrapper<MaintenanceAvailabilityDecision>>(`${this.baseUrl}/availability/check`, request);
  }

  recomputeAvailability(owningCompanyId: number, vehicleId: number): Observable<APIResponseWrapper<MaintenanceVehicleAvailability>> {
    return this.http.post<APIResponseWrapper<MaintenanceVehicleAvailability>>(`${this.baseUrl}/availability/recompute`, null, {
      params: new HttpParams().set('owningCompanyId', owningCompanyId).set('vehicleId', vehicleId)
    });
  }

  getPartAvailability(owningCompanyId: number, warehouseItemId: number, warehouseId?: number, requestedQuantity?: number): Observable<APIResponseWrapper<MaintenancePartAvailability>> {
    let params = new HttpParams()
      .set('owningCompanyId', owningCompanyId)
      .set('warehouseItemId', warehouseItemId);
    params = this.appendIfPresent(params, 'warehouseId', warehouseId);
    params = this.appendIfPresent(params, 'requestedQuantity', requestedQuantity);
    return this.http.get<APIResponseWrapper<MaintenancePartAvailability>>(`${this.baseUrl}/parts/availability`, { params });
  }

  searchReorderRecommendations(filters: MaintenanceSearchParams & { warehouseItemId?: number; workOrderId?: number }): Observable<APIListWrapper<MaintenancePartReorderRecommendation>> {
    return this.http.get<APIListWrapper<MaintenancePartReorderRecommendation>>(`${this.baseUrl}/parts/reorder-recommendations`, { params: this.searchParams(filters) });
  }

  getFinanceSummary(owningCompanyId: number, vehicleId?: number): Observable<APIResponseWrapper<MaintenanceFinanceSummary>> {
    let params = new HttpParams().set('owningCompanyId', owningCompanyId);
    params = this.appendIfPresent(params, 'vehicleId', vehicleId);
    return this.http.get<APIResponseWrapper<MaintenanceFinanceSummary>>(`${this.baseUrl}/finance-summary`, { params });
  }

  private searchParams(filters: MaintenanceSearchParams): HttpParams {
    let params = new HttpParams()
      .set('page', filters.page ?? 0)
      .set('pageSize', filters.pageSize ?? 20);
    const extendedFilters = filters as unknown as Record<string, unknown>;
    for (const key of ['owningCompanyId', 'status', 'severity', 'priority', 'vehicleId', 'driverId', 'vehicleTypeId', 'requestId', 'programId', 'formId', 'submissionId', 'source', 'purchaseType', 'keyword', 'warehouseItemId', 'workOrderId']) {
      params = this.appendIfPresent(params, key, extendedFilters[key]);
    }
    return params;
  }

  private appendIfPresent(params: HttpParams, key: string, value: unknown): HttpParams {
    if (value === null || value === undefined || value === '') {
      return params;
    }
    return params.set(key, value instanceof Date ? value.toISOString() : value.toString());
  }
}
